import { userStore } from "@entities/user/model/userStore";
import { openLoginModalFromExternal } from "@features/authV2/model/store.ts";
import { openAccountAccessIssueModal } from "@features/facebook-asid-recovery/ui/AccountAccessIssueModal.model.ts";
import { handlePersonalInfoRequirements } from "@features/payment/lib/helpers.ts";
import { openStripeCheckoutModal } from "@features/payment/ui/OpenStripeCheckoutModal.tsx";
import { openReportIssueModal } from "@features/report-issue/ui/OpenReportIssueModal.tsx";
import { sharingHandlersStore } from "@features/share/model/ShareHandlersStore/index.ts";
import { openLinkShareModal } from "@features/share-link/ui/OpenLinkShareModal.tsx";
import { openPushPermissionModal } from "@features/subscribe-push/ui/OpenPushPermissionModal.tsx";
import {
	FxIdDomainStoreEnumsSupportedWebPublishingPlatform,
	FxIdWebFeaturesGamesChooseGamePortalConfig,
	FxIdWebFeaturesGamesChooseGameResponsePublicGameData,
	ISessionAnalyticsData
} from "@fxid/openapi-client";
import OpenApiClient from "@fxid/openapi-client";
import type { IFxIdSdkDependencies } from "@fxid/sdk";
import { FxIdSdk, IframeType, IframeTypeToSupportedPlatformMap } from "@fxid/sdk";
import { IFxIdSdkAdapterSocialSettings } from "@fxid/sdk-core";
import { UseOSReturnValue } from "@mantine/hooks";
import { openIframeModal } from "@shared/ui/Modal/OpenIframeModal.tsx";
import { openInfoModal } from "@shared/ui/Modal/OpenInfoModal.tsx";
import { openTextCopyModal } from "@shared/ui/Modal/OpenTextCopyModal.tsx";
import log from "loglevel";
import { useEffect } from "react";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

/**
 * @description Конфигурационный объект для инициализации FxIdSdk.
 * Его структура в точности соответствует параметрам конструктора SDK.
 */
export interface FxIdSdkConfig {
	game: string;
	adapter: FxIdDomainStoreEnumsSupportedWebPublishingPlatform;
	portalConfig: FxIdWebFeaturesGamesChooseGamePortalConfig;
	gameConfig: FxIdWebFeaturesGamesChooseGameResponsePublicGameData;
	os: UseOSReturnValue;
	isMobile: boolean;
}

// --- Интерфейс состояния Zustand ---

export interface IWebPlayerState {
	// --- Состояние FxId SDK ---
	adapter: IframeType;
	/** Экземпляр инициализированного SDK. */
	fxidInstance: FxIdSdk | null;
	/** Полученные социальные настройки от SDK. */
	fxidSettings: IFxIdSdkAdapterSocialSettings | null;
	/** Флаг, указывающий на то, что идет процесс инициализации SDK. */
	isFxidLoading: boolean;
	/** Флаг, указывающий на то, что SDK успешно инициализирован. */
	isFxidInitialized: boolean;
	/** Объект ошибки, если инициализация SDK не удалась. */
	fxidError: Error | null;
	/** Асинхронный action для запуска процесса инициализации SDK. */
	initializeFxid: (config: FxIdSdkConfig) => Promise<void>;

	// --- Аналитика сессии ---
	/** ID сессии для аналитики. */
	sessionId: string;
	/** Время начала сессии (timestamp в миллисекундах). */
	sessionStartTime: number;
	/** Получить время от начала сессии в секундах. */
	getTimeFromSessionStart: () => number;
	/** Получить данные сессии для аналитики. */
	getSessionAnalyticsData: () => ISessionAnalyticsData;
}

// Определяем адаптер по умолчанию из переменных окружения или ставим 'fxid'.
const appDefaultAdapter: IframeType = (import.meta.env.VITE_APP_DEFAULT_ADAPTER ?? "fxid") as IframeType;
const defaultPlatform = IframeTypeToSupportedPlatformMap[appDefaultAdapter];

/**
 * Создает хранилище состояния для веб-плеера.
 * Использует middleware 'immer' для упрощения работы с состоянием.
 */
// Генерируем ID сессии и время начала сессии
const generateSessionId = () => {
	return `${Math.random().toString(36).substring(2, 15)}`;
};

export const useWebPlayerStore = create<IWebPlayerState>()(
	immer<IWebPlayerState>((set, get) => ({
		// --- Начальное состояние ---
		adapter: appDefaultAdapter,
		platform: defaultPlatform,
		game: "",

		fxidInstance: null,
		fxidSettings: null,
		isFxidLoading: false,
		isFxidInitialized: false,
		fxidError: null,

		// Аналитика сессии
		sessionId: generateSessionId(),
		sessionStartTime: Date.now(),
		getTimeFromSessionStart: () => {
			const state = get();
			return (Date.now() - state.sessionStartTime) / 1000;
		},
		getSessionAnalyticsData: () => {
			const state = get();
			return {
				sessionId: state.sessionId,
				timeFromSessionStart: state.getTimeFromSessionStart()
			};
		},

		// --- Действия ---

		/**
		 * Инициализирует FxId SDK.
		 * Эта функция является идемпотентной: она выполнит логику инициализации только один раз.
		 */
		initializeFxid: async (config) => {
			if (get().isFxidLoading || get().isFxidInitialized) {
				log.debug("FxIdSdk initialization is already in progress or completed");
				return;
			}

			set((state) => {
				state.isFxidLoading = true;
				state.fxidError = null;
			});

			log.debug("Starting FxIdSdk initialization");

			try {
				// Деструктурируем конфиг для передачи в конструктор.
				// Имена полей в config должны совпадать с параметрами конструктора.
				const { game, adapter, portalConfig, gameConfig, os, isMobile } = config;

				// Создаем полные dependencies для SDK
				const dependencies: IFxIdSdkDependencies = {
					// UI Callbacks
					openTextCopyModal,
					openLoginModal: openLoginModalFromExternal,
					openReportIssueModal: (params) => {
						openReportIssueModal({
							gameData: (params.gameData as string) ?? game
						});
					},
					openPushPermissionModal: async (params) => {
						openPushPermissionModal({
							gameName: game,
							serverKey: params.serverKey ?? "",
							onPushSubscriptionSuccess: params.onPushSubscriptionSuccess ?? (() => { }),
							onPushSubscriptionFail: params.onPushSubscriptionFail ?? (() => { })
						});
					},
					openLinkShareModal: (params) => {
						openLinkShareModal({
							link: params.link ?? ""
						});
					},
					openIframeModal: (params) => {
						openIframeModal({
							url: params.url,
							onClose: params.onClose,
							fullScreen: params.fullScreen ?? false
						});
					},
					openStripeCheckoutModal: (params) => {
						if (!params.paymentSecret || !params.publishableKey || !params.returnUrl) {
							log.error("Missing required params for Stripe Checkout Modal", params);
							return;
						}
						openStripeCheckoutModal({
							paymentSecret: params.paymentSecret,
							publishableKey: params.publishableKey,
							returnUrl: params.returnUrl,
							email: params.email,
							onCompleted: params.onCompleted ?? (() => { }),
							onError: (message) => {
								log.error("Stripe payment error", message);
								params.onError?.(message);
							},
							onClose: params.onClose
						});
					},
					openInfoModal: (params) => {
						openInfoModal(params.message);
					},
					openAccountAccessIssueModal,

					// Data Callbacks
					getSharingHandler: () => {
						const { enableShareVk, enableShareOk, enableShareTelegram, enableLinkCopy } = sharingHandlersStore.getState();
						return {
							share: async () => { },
							enableShareVk,
							enableShareOk,
							enableShareTelegram,
							enableLinkCopy
						};
					},

					setUserId: (id) => {
						log.debug("FxIdSdk setUserId", id);
						userStore.getState().updateUserId(id);
					},
					setUserDisplayName: (name) => {
						log.debug("FxIdSdk setUserDisplayName", name);
						userStore.getState().updateDisplayName(name);
					},

					// Feature Callbacks
					handlePersonalInfoRequirements: async (requirements) => {
						const sdk = get().fxidInstance;
						if (sdk) {
							await handlePersonalInfoRequirements(sdk, game, get().adapter, requirements, false, "vertical");
						} else {
							log.error("FxIdSdk instance not found when handling personal info requirements");
						}
					},

					// Analytics Callbacks
					getSessionData: () => get().getSessionAnalyticsData()
				};

				// Вызов конструктора с точными, типизированными параметрами.
				const sdkInstance = new FxIdSdk(
					OpenApiClient,
					game,
					adapter,
					portalConfig,
					gameConfig,
					{ os: os },
					isMobile,
					dependencies
				);
				// InitializeSocialAdapter теперь возвращает social settings напрямую
				log.debug("FxIdSdk initializing social adapter");

				await sdkInstance.InitializeSocialAdapter();
				await sdkInstance.LoginToFxId();

				const settings = await sdkInstance.SocialSettings();

				log.debug("FxIdSdk initialization and settings fetch successful");

				window.FxIdSdk = sdkInstance;

				set((state) => {
					state.fxidInstance = sdkInstance;
					state.fxidSettings = settings;
					state.isFxidInitialized = true;
					state.isFxidLoading = false;
				});
			} catch (err) {
				// Многие платформенные SDK (в первую очередь FB Instant Games — FB.APIError)
				// бросают plain object вида { code, message, extraData }, который НЕ является
				// instanceof Error. Если оборачивать всё в синтетический Error("unknown..."),
				// мы теряем code/message/extraData — и реальный диагноз (например
				// "User is not eligible for shield") виден только при ручном разворачивании
				// объекта в DevTools. Поэтому извлекаем известные поля и логируем их structured.
				const errAsRecord = (err ?? {}) as Record<string, unknown>;
				const platformErrorCode = typeof errAsRecord.code === "string" ? errAsRecord.code : undefined;
				const platformErrorMessage = typeof errAsRecord.message === "string" ? errAsRecord.message : undefined;
				const platformErrorExtraData = errAsRecord.extraData;

				log.error(
					"Failed to initialize FxIdSdk: code=%s, message=%s, extraData=%o, raw=%o",
					platformErrorCode ?? "(no code)",
					platformErrorMessage ?? "(no message)",
					platformErrorExtraData,
					err
				);

				// Сохраняем структурированную ошибку, переиспользуя реальный текст от платформы
				// если он есть — UI и Sentry получают читаемый текст вместо обезличенного
				// "An unknown error occurred...". Error-инстансы пробрасываем как есть, чтобы
				// сохранить stack trace.
				const error =
					err instanceof Error
						? err
						: new Error(
								platformErrorMessage != null
									? `[${platformErrorCode ?? "UNKNOWN"}] ${platformErrorMessage}`
									: "An unknown error occurred during FxIdSdk initialization"
							);
				set((state) => {
					state.fxidError = error;
					state.isFxidLoading = false;
					state.isFxidInitialized = false; // Позволяет повторить попытку
				});
			}
		}
	}))
);
export const useWebPlayerUiStore = useWebPlayerStore;

/**
 * Хук-эффект для запуска инициализации FxId SDK при монтировании компонента.
 * Этот хук следует использовать **один раз** в корневом компоненте вашего приложения (например, App.tsx).
 */
export const useInitializeFxId = (config: FxIdSdkConfig | null | undefined) => {
	if (config == null) {
		log.info("useInitializeFxId - no config yet");
		return null;
	}

	const initialize = useWebPlayerStore((state) => state.initializeFxid);

	useEffect(() => {
		void initialize(config);
	}, [initialize]);
};
